import React, { useMemo, memo } from "react";
import {
  Pencil, Pen, BookOpen, Book, Ruler, Backpack, GraduationCap, Calculator,
  Palette, Paintbrush, FlaskConical, Atom, Globe, Apple, Scissors, Compass,
  Microscope, NotebookPen, PencilRuler, Trophy,
} from "lucide-react";
import { getBackground } from "@/lib/backgrounds";

export const BG_SPEEDS = [
  { id: "slow", name: "Lenta" },
  { id: "normal", name: "Normal" },
  { id: "fast", name: "Rápida" },
];
export const DEFAULT_BG_SPEED = "normal";
const SPEED_FACTOR = { slow: 1.8, normal: 1, fast: 0.45 };

// PRNG determinista por id para posiciones estables.
function seeded(seedStr) {
  let h = 2166136261;
  for (let i = 0; i < seedStr.length; i++) { h ^= seedStr.charCodeAt(i); h = Math.imul(h, 16777619); }
  return () => { h += 0x6D2B79F5; let t = h; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function arr(n, rnd, fn) { return Array.from({ length: n }, (_, i) => fn(i, rnd)); }

const ICON_SETS = {
  mixed: [Pencil, BookOpen, Ruler, Backpack, GraduationCap, Calculator, Globe, FlaskConical, Palette, Scissors, Compass, Trophy],
  pencils: [Pencil, Pen, NotebookPen, PencilRuler, Ruler, Compass],
  books: [Book, BookOpen, NotebookPen, GraduationCap],
  science: [FlaskConical, Atom, Microscope, Globe, Compass],
  math: [Calculator, Ruler, Compass, PencilRuler],
  art: [Palette, Paintbrush, Pencil, Scissors],
  grad: [GraduationCap, Book, Trophy, Globe],
};

function Lines({ p, sf }) {
  const ink = p.ink || "17,34,64";
  const c1 = `rgba(${ink},${p.scheme === "dark" ? 0.06 : 0.05})`;
  const c2 = `rgba(${ink},${p.scheme === "dark" ? 0.04 : 0.035})`;
  let bg, bg2;
  if (p.variant === "horizontal") {
    bg = `repeating-linear-gradient(0deg, ${c1} 0, ${c1} 1px, transparent 1px, transparent 38px)`;
    bg2 = `repeating-linear-gradient(90deg, ${c2} 0, ${c2} 1px, transparent 1px, transparent 120px)`;
  } else if (p.variant === "mesh") {
    bg = `repeating-linear-gradient(0deg, ${c1} 0, ${c1} 1px, transparent 1px, transparent 44px), repeating-linear-gradient(90deg, ${c1} 0, ${c1} 1px, transparent 1px, transparent 44px)`;
    bg2 = "none";
  } else {
    const gap = p.variant === "diag-wide" ? 80 : 46;
    bg = `repeating-linear-gradient(45deg, ${c1} 0, ${c1} 1px, transparent 1px, transparent ${gap}px), repeating-linear-gradient(-45deg, ${c1} 0, ${c1} 1px, transparent 1px, transparent ${gap}px)`;
    bg2 = "none";
  }
  return (
    <>
      <div className="tbg-layer" style={{ inset: "-60%", backgroundImage: bg, animation: `tbg-drift-slow ${30 * sf}s linear infinite` }} />
      {bg2 !== "none" && <div className="tbg-layer" style={{ inset: "-60%", backgroundImage: bg2, animation: `tbg-slide-x ${26 * sf}s linear infinite` }} />}
    </>
  );
}

function Waves({ p, sf }) {
  const ink = p.ink || "30,58,138";
  return (
    <svg className="tbg-layer" style={{ width: "200%", height: "100%" }} preserveAspectRatio="none" viewBox="0 0 1440 800">
      {[0, 1, 2].map((i) => (
        <path key={i} d={`M0 ${300 + i * 120} C 360 ${240 + i * 120}, 720 ${360 + i * 120}, 1440 ${300 + i * 120} L1440 800 L0 800 Z`}
          fill={`rgba(${ink},${0.05 + i * 0.02})`}
          style={{ transformOrigin: "center", animation: `tbg-slide-x ${(28 + i * 8) * sf}s linear infinite`, animationDirection: i % 2 ? "reverse" : "normal" }} />
      ))}
    </svg>
  );
}

function Bubbles({ p, sf }) {
  const rnd = useMemo(() => seeded(p.id), [p.id]);
  const items = useMemo(() => arr(16, rnd, (i, r) => ({
    left: r() * 100, size: 40 + r() * 160, dur: 16 + r() * 22, delay: -r() * 30,
    color: p.colors[i % p.colors.length], op: 0.12 + r() * 0.18,
  })), [p.id]);
  return items.map((b, i) => (
    <div key={i} className="tbg-el" style={{
      left: `${b.left}%`, bottom: "-20vh", width: b.size, height: b.size, borderRadius: "9999px",
      background: `radial-gradient(circle at 35% 30%, ${b.color}, transparent 70%)`, opacity: b.op,
      filter: "blur(2px)", animation: `tbg-rise ${b.dur * sf}s linear ${b.delay * sf}s infinite`,
    }} />
  ));
}

function Rings({ p, sf }) {
  const rnd = useMemo(() => seeded(p.id), [p.id]);
  const items = useMemo(() => arr(14, rnd, (i, r) => ({
    left: r() * 100, top: r() * 100, size: 60 + r() * 220, dur: 18 + r() * 20, delay: -r() * 20,
  })), [p.id]);
  return items.map((b, i) => (
    <div key={i} className="tbg-el" style={{
      left: `${b.left}%`, top: `${b.top}%`, width: b.size, height: b.size, borderRadius: "9999px",
      border: `1.5px solid rgba(${p.ink},${p.scheme === "dark" ? 0.12 : 0.09})`,
      animation: `tbg-float ${b.dur * sf}s ease-in-out ${b.delay * sf}s infinite`,
    }} />
  ));
}

function Dots({ p, sf }) {
  const c = `rgba(${p.ink},${p.scheme === "dark" ? 0.14 : 0.10})`;
  return <div className="tbg-layer" style={{
    inset: "-60%", backgroundImage: `radial-gradient(${c} 1.5px, transparent 1.6px)`, backgroundSize: "34px 34px",
    animation: `tbg-drift-slow ${28 * sf}s linear infinite`,
  }} />;
}

function Icons({ p, sf }) {
  const set = ICON_SETS[p.set] || ICON_SETS.mixed;
  const rnd = useMemo(() => seeded(p.id), [p.id]);
  const items = useMemo(() => arr(22, rnd, (i, r) => ({
    left: r() * 96, top: r() * 92, size: 22 + r() * 40, dur: 12 + r() * 16, delay: -r() * 18,
    Icon: set[i % set.length], op: 0.06 + r() * 0.1, anim: r() > 0.5 ? "tbg-sway" : "tbg-float",
  })), [p.id]);
  return items.map((b, i) => {
    const I = b.Icon;
    return <I key={i} className="tbg-el" strokeWidth={1.5}
      style={{ left: `${b.left}%`, top: `${b.top}%`, width: b.size, height: b.size, color: `rgba(${p.ink},1)`, opacity: b.op, animation: `${b.anim} ${b.dur * sf}s ease-in-out ${b.delay * sf}s infinite` }} />;
  });
}

function GradientShift({ p, sf }) {
  const grad = `linear-gradient(120deg, ${p.colors.join(", ")})`;
  return <div className="tbg-layer" style={{
    background: grad, backgroundSize: "300% 300%",
    animation: `tbg-pan ${18 * sf}s ease infinite${p.hue ? `, tbg-hue ${24 * sf}s linear infinite` : ""}`,
  }} />;
}

function Clouds({ p, sf }) {
  const rnd = useMemo(() => seeded(p.id), [p.id]);
  const items = useMemo(() => arr(9, rnd, (i, r) => ({
    top: r() * 80, w: 220 + r() * 340, h: 90 + r() * 120, dur: 40 + r() * 50, delay: -r() * 40, op: 0.4 + r() * 0.5,
  })), [p.id]);
  return (
    <>
      <div className="tbg-layer" style={{ background: `linear-gradient(180deg, ${p.base}, ${p.sky2 || p.base})` }} />
      {items.map((b, i) => (
        <div key={i} className="tbg-el" style={{
          top: `${b.top}%`, left: "-30%", width: b.w, height: b.h, borderRadius: "9999px",
          background: p.cloud, opacity: b.op, filter: "blur(30px)",
          animation: `tbg-slide-x ${b.dur * sf}s linear ${b.delay * sf}s infinite`, animationDirection: "reverse",
        }} />
      ))}
    </>
  );
}

function Grain({ p, sf }) {
  const c = `rgba(${p.ink},${p.scheme === "dark" ? 0.05 : 0.045})`;
  return <div className="tbg-layer" style={{
    inset: "-40%", backgroundImage: `radial-gradient(${c} 1px, transparent 1px)`, backgroundSize: "5px 5px",
    animation: `tbg-drift-slow ${90 * sf}s linear infinite`,
  }} />;
}

function Spotlight({ p, sf }) {
  const g = p.glow || "59,130,246";
  return (
    <>
      <div className="tbg-el" style={{ left: "-10vw", top: "-12vw", width: "48vw", height: "48vw", borderRadius: "9999px", background: `radial-gradient(circle, rgba(${g},${p.scheme === "dark" ? 0.35 : 0.14}), transparent 70%)`, filter: "blur(60px)", animation: `tbg-float ${26 * sf}s ease-in-out infinite` }} />
      <div className="tbg-el" style={{ right: "-8vw", bottom: "-10vw", width: "42vw", height: "42vw", borderRadius: "9999px", background: `radial-gradient(circle, rgba(${g},${p.scheme === "dark" ? 0.25 : 0.10}), transparent 70%)`, filter: "blur(70px)", animation: `tbg-float ${34 * sf}s ease-in-out infinite reverse` }} />
    </>
  );
}

function Geo({ p, sf }) {
  const rnd = useMemo(() => seeded(p.id), [p.id]);
  const items = useMemo(() => arr(16, rnd, (i, r) => ({
    left: r() * 94, top: r() * 90, size: 30 + r() * 90, dur: 20 + r() * 24, delay: -r() * 20, rev: r() > 0.5,
  })), [p.id]);
  const col = `rgba(${p.ink},${p.scheme === "dark" ? 0.14 : 0.10})`;
  const clip = { tri: "polygon(50% 0, 100% 100%, 0 100%)", hex: "polygon(25% 0,75% 0,100% 50%,75% 100%,25% 100%,0 50%)", diamond: "polygon(50% 0,100% 50%,50% 100%,0 50%)", square: "none" }[p.shape] || "none";
  return items.map((b, i) => (
    <div key={i} className="tbg-el" style={{
      left: `${b.left}%`, top: `${b.top}%`, width: b.size, height: b.size,
      border: `1.5px solid ${col}`, clipPath: clip === "none" ? undefined : clip,
      animation: `${b.rev ? "tbg-spin-rev" : "tbg-spin"} ${b.dur * sf}s linear ${b.delay * sf}s infinite`,
    }} />
  ));
}

function Particles({ p, sf, twinkle }) {
  const rnd = useMemo(() => seeded(p.id), [p.id]);
  const items = useMemo(() => arr(48, rnd, (i, r) => ({
    left: r() * 100, top: r() * 100, size: 2 + r() * 4, dur: 6 + r() * 10, delay: -r() * 12,
  })), [p.id]);
  return items.map((b, i) => (
    <div key={i} className="tbg-el" style={{
      left: `${b.left}%`, top: `${b.top}%`, width: b.size, height: b.size, borderRadius: "9999px",
      background: `rgba(${p.ink},0.8)`, animation: `${twinkle ? "tbg-twinkle" : "tbg-float"} ${b.dur * sf}s ease-in-out ${b.delay * sf}s infinite`,
    }} />
  ));
}

function Snow({ p, sf }) {
  const rnd = useMemo(() => seeded(p.id), [p.id]);
  const items = useMemo(() => arr(50, rnd, (i, r) => ({
    left: r() * 100, size: 3 + r() * 6, dur: 8 + r() * 12, delay: -r() * 14, op: 0.4 + r() * 0.5,
  })), [p.id]);
  return items.map((b, i) => (
    <div key={i} className="tbg-el" style={{
      left: `${b.left}%`, top: "-5vh", width: b.size, height: b.size, borderRadius: "9999px",
      background: `rgba(${p.ink},${b.op})`, animation: `tbg-fall ${b.dur * sf}s linear ${b.delay * sf}s infinite`,
    }} />
  ));
}

function Watermark({ p, sf, url }) {
  if (!url) {
    return <div className="tbg-layer flex items-center justify-center">
      <span style={{ color: `rgba(${p.scheme === "dark" ? "255,255,255" : "17,34,64"},0.25)` }} className="text-sm">Sube tu logo en Ajustes → Marca de agua</span>
    </div>;
  }
  if (p.wm === "tiled") {
    return <div className="tbg-layer" style={{
      inset: "-20%", backgroundImage: `url(${url})`, backgroundRepeat: "repeat", backgroundSize: "160px",
      opacity: p.scheme === "dark" ? 0.10 : 0.07, animation: `tbg-wm-pan ${40 * sf}s linear infinite`,
      filter: p.scheme === "dark" ? "invert(1) brightness(2)" : "none",
    }} />;
  }
  return (
    <div className="tbg-layer flex items-center justify-center">
      <img src={url} alt="" style={{
        width: "42vw", maxWidth: 560, opacity: p.scheme === "dark" ? 0.10 : 0.07,
        animation: `tbg-wm-float ${24 * sf}s ease-in-out infinite`,
        filter: p.scheme === "dark" ? "invert(1) brightness(2)" : "none",
      }} />
    </div>
  );
}

const RENDERERS = { lines: Lines, waves: Waves, bubbles: Bubbles, rings: Rings, dots: Dots, icons: Icons, gradient: GradientShift, clouds: Clouds, grain: Grain, spotlight: Spotlight, geo: Geo, snow: Snow };

function ThemedBackground({ bgId, watermarkUrl, speed, preview = false }) {
  const p = getBackground(bgId);
  if (!p) return null;
  const sf = SPEED_FACTOR[speed] || SPEED_FACTOR[DEFAULT_BG_SPEED];

  let content;
  if (p.type === "watermark") content = <Watermark p={p} sf={sf} url={watermarkUrl} />;
  else if (p.type === "particles") content = <Particles p={p} sf={sf} twinkle={p.id !== "part-light" && p.id !== "part-dark"} />;
  else { const R = RENDERERS[p.type]; content = R ? <R p={p} sf={sf} /> : null; }

  const showBase = p.type !== "gradient" && p.type !== "clouds";
  return (
    <div key={p.id} className={preview ? "tbg-abs" : "tbg"} aria-hidden="true" data-testid="themed-background" data-bg={p.id}>
      {showBase && <div className="tbg-layer" style={{ background: p.base }} />}
      {content}
    </div>
  );
}

export default memo(ThemedBackground);
